import { JsonObject } from '@bufbuild/protobuf';
import { StalkerSettings } from '@q6cyber/proto2es/generated/client';

import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { RouterInput, RouterOutput } from '../../../utils';
import { CustomFormArray, customFormGroup } from '../utilities';
import {
  LongNumberValue,
  ProtoNumberKind,
  disallowedCharactersValidator,
  protoNumberValidator,
  storedLongValidator,
} from '../validations';
import {
  ENTITLEMENT_REQUIREMENTS,
  entitlementLabel,
  entitlementRequirementsErrorMessage,
} from './entitlements.interface';

/** Continental-US center — the default map location for new clients. */
export const US_LAT_COORD = '37.09024';
export const US_LONG_COORD = '-95.712891';
export const DEFAULT_MAP_CENTER = `${US_LAT_COORD}, ${US_LONG_COORD}`;

/** Keycloak types `roles` as a plain `string[]`, so this is a UI option list only. */
export const PORTAL_USER_ROLES = [
  'SUPER_ADMIN',
  'ADMIN',
  'USER',
  'TEAM_MANAGER',
] as const;

export type PortalUserRole = (typeof PORTAL_USER_ROLES)[number];

export type PortalConfigResponse = RouterOutput['portalConfig']['get'];

export type PortalUser = PortalConfigResponse['users'][number];

/** What `portalUsers.create` accepts, derived from the router input so the two stay in step. */
export type PortalUserNode = RouterInput['portalUsers']['create']['node'];

// `entitlements` carries a zod default, so the input type has it optional.
export type PortalUserEntitlement = NonNullable<
  PortalUserNode['entitlements']
>[number];

export type PortalUserRoleName = PortalUserNode['roles'][number];

/** Spread by callers building a user from partial input, so no field arrives `undefined`. */
export const DEFAULT_PORTAL_USER = {
  firstName: '',
  lastName: '',
  email: '',
  roles: [],
  entitlements: [],
};

/** A light projection — the `StalkerSettings` proto only arrives from `get`, so there is
 * nothing to proto-parse and the row type is just the tRPC edge. */
export type PortalConfigListResponse =
  RouterOutput['portalConfig']['list']['edges'][0];

/** What Dragonfly seeded the impact algorithm with for a client that had never been scored. */
export const DEFAULT_IMPACT_SETTINGS = {
  activeCards: true,
  activeCardsOverride: 70,
  activeAtos: true,
  costOfEngagement: 0,
  fraudCostPerCard: 0,
  showCardsImpactSavings: true,
  showAtosImpactSavings: true,
};

/** Portal defaults for a client that has never been onboarded. */
export const DEFAULT_PORTAL_CONFIG = new StalkerSettings()
  .fromJson({
    entitlements: [],
    display: {
      defaultLanguage: 'EN',
      mapCenterLocation: DEFAULT_MAP_CENTER,
    },
    clientSettings: {
      mfa: { required: false },
      externalSso: { enabled: false },
    },
    impact: DEFAULT_IMPACT_SETTINGS,
  })
  .toJson({ emitDefaultValues: true }) as JsonObject;

/** The selected entitlements whose required one (`ENTITLEMENT_REQUIREMENTS`) is not selected. */
export const missingEntitlementRequirements = (
  entitlements: string[] | null | undefined
): string[] =>
  Array.isArray(entitlements)
    ? entitlements.filter(
        entitlement =>
          entitlement in ENTITLEMENT_REQUIREMENTS &&
          !entitlements.includes(ENTITLEMENT_REQUIREMENTS[entitlement])
      )
    : [];

export const portalEntitlementsValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const entitlements = (control.value ?? []) as string[];

  if (!entitlements.length) {
    return { required: true };
  }

  const dependents = missingEntitlementRequirements(entitlements);

  return dependents.length
    ? { invalidEntitlementRequirement: { dependents } }
    : null;
};

/** Every deliverables picker. `Validators.required` is listed as well as enforced, since
 * that is what Material reads to mark the field required. */
export const portalEntitlementsControl = () =>
  new FormControl<string[]>([], {
    nonNullable: true,
    validators: [Validators.required, portalEntitlementsValidator],
  });

export type PortalConfigFormGroup = ReturnType<typeof portalConfigForm>;

/** Free text, and `nonNullable` so a reset gives back a list rather than a null. */
const feedbackChips = () =>
  new FormControl<string[]>([], { nonNullable: true });

/** `client.EntitlementCustomFeedbackValid` — the statuses a client can relabel. */
const customFeedbackValidForm = () =>
  new FormGroup({
    active: feedbackChips(),
    previouslyCancelled: feedbackChips(),
    neverIssued: feedbackChips(),
    unidentified: feedbackChips(),
    activeWoSuspiciousActivity: feedbackChips(),
    activeWSuspiciousActivity: feedbackChips(),
    closed: feedbackChips(),
    doesNotExist: feedbackChips(),
    inactive: feedbackChips(),
    noMatch: feedbackChips(),
    pending: feedbackChips(),
  });

/** `client.EntitlementCustomFeedbackAction` — the actions a client can relabel. */
const customFeedbackActionForm = () =>
  new FormGroup({
    reissue: feedbackChips(),
    watch: feedbackChips(),
    none: feedbackChips(),
    other: feedbackChips(),
    pending: feedbackChips(),
    reset: feedbackChips(),
    contact: feedbackChips(),
  });

const customFeedbackEntitlementForm = () =>
  new FormGroup({
    valid: customFeedbackValidForm(),
    action: customFeedbackActionForm(),
  });

/** `client.CustomFeedbackValues`. Written out so the controls keep their names in the type,
 * upper case to match Intellihawq, which is how they arrive in the JSON. */
const customFeedbackValuesForm = () =>
  new FormGroup({
    CARDS: customFeedbackEntitlementForm(),
    ATO: customFeedbackEntitlementForm(),
    CE: customFeedbackEntitlementForm(),
    FRAUD_ALERTS: customFeedbackEntitlementForm(),
    IOC: customFeedbackEntitlementForm(),
  });

/** One row is one label (`custom`) filed under one proto field (`option`), and a field can
 * carry several. `values` is the saved shape; this is the shape the user edits. */
const customFeedbackRowsForm = () =>
  new FormGroup({
    CARDS: customFeedbackEntitlementRowsForm(),
    ATO: customFeedbackEntitlementRowsForm(),
    CE: customFeedbackEntitlementRowsForm(),
    FRAUD_ALERTS: customFeedbackEntitlementRowsForm(),
    IOC: customFeedbackEntitlementRowsForm(),
  });

const customFeedbackEntitlementRowsForm = () =>
  new FormGroup({
    valid: new CustomFormArray<CustomFeedbackRowFormGroup>(
      [],
      customFeedbackRowForm
    ),
    action: new CustomFormArray<CustomFeedbackRowFormGroup>(
      [],
      customFeedbackRowForm
    ),
  });

/** `rows` is UI-only. It sits in the form so disabling, dirty tracking and validation reach
 * it like any other control; `ignoreUnknownFields` keeps it out of the payload. */
const customFeedbackForm = () =>
  new FormGroup(
    {
      isActive: new FormControl(false, { nonNullable: true }),
      values: customFeedbackValuesForm(),
      rows: customFeedbackRowsForm(),
    },
    { validators: [customFeedbackValidator] }
  );

export type CustomFeedbackFormGroup = ReturnType<typeof customFeedbackForm>;

export const CUSTOM_FEEDBACK_GROUPS = ['valid', 'action'] as const;

export type CustomFeedbackGroup = (typeof CUSTOM_FEEDBACK_GROUPS)[number];

export const CUSTOM_FEEDBACK_GROUP_LABELS: Record<CustomFeedbackGroup, string> =
  {
    valid: 'Status',
    action: 'Actions',
  };

export type CustomFeedbackEntitlement = keyof ReturnType<
  typeof customFeedbackValuesForm
>['controls'];

export type CustomFeedbackValidField = keyof ReturnType<
  typeof customFeedbackValidForm
>['controls'];

export type CustomFeedbackActionField = keyof ReturnType<
  typeof customFeedbackActionForm
>['controls'];

/** The `client.Entitlement` behind each key. The feedback fields carry a `json_name`, so
 * both protos spell every one of them the same way. */
export const CUSTOM_FEEDBACK_ENTITLEMENT: Record<
  CustomFeedbackEntitlement,
  string
> = {
  CARDS: 'CARDS',
  ATO: 'ATO',
  CE: 'CE',
  FRAUD_ALERTS: 'FRAUD_ALERTS',
  IOC: 'IOC',
};

/**
 * Field labels, in the order the section renders them. Keyed off the form controls, so a name
 * that is not a control is a compile error rather than a row that silently renders empty.
 */
export const CUSTOM_FEEDBACK_VALID_LABELS: Record<
  CustomFeedbackValidField,
  string
> = {
  active: 'Active',
  activeWSuspiciousActivity: 'Active w/ Suspicious Activity',
  activeWoSuspiciousActivity: 'Active w/o Suspicious Activity',
  inactive: 'Inactive',
  closed: 'Closed',
  previouslyCancelled: 'Previously Cancelled',
  neverIssued: 'Never Issued',
  doesNotExist: 'Does Not Exist',
  unidentified: 'Unidentified',
  noMatch: 'No Match',
  pending: 'Pending',
};

export const CUSTOM_FEEDBACK_ACTION_LABELS: Record<
  CustomFeedbackActionField,
  string
> = {
  reissue: 'Reissue',
  reset: 'Reset',
  watch: 'Watch',
  contact: 'Contact',
  pending: 'Pending',
  other: 'Other',
  none: 'None',
};

/** All five draw on the same two protos, but only some fields suit each — Cards has no
 * `closed`, ATO no `neverIssued` — so the picker is per deliverable. */
const ATO_VALID_OPTIONS: readonly CustomFeedbackValidField[] = [
  'activeWoSuspiciousActivity',
  'activeWSuspiciousActivity',
  'closed',
  'doesNotExist',
  'pending',
];

const ATO_ACTION_OPTIONS: readonly CustomFeedbackActionField[] = [
  'pending',
  'watch',
  'reset',
  'contact',
  'other',
  'none',
];

export const CUSTOM_FEEDBACK_OPTIONS: Record<
  CustomFeedbackEntitlement,
  {
    valid: readonly CustomFeedbackValidField[];
    action: readonly CustomFeedbackActionField[];
  }
> = {
  CARDS: {
    valid: [
      'active',
      'previouslyCancelled',
      'neverIssued',
      'unidentified',
      'pending',
    ],
    action: ['reissue', 'watch', 'none', 'other', 'pending'],
  },
  ATO: { valid: ATO_VALID_OPTIONS, action: ATO_ACTION_OPTIONS },
  CE: {
    valid: ['active', 'inactive', 'noMatch', 'pending'],
    action: ['pending', 'reset', 'contact', 'other', 'none'],
  },
  // Fraud Alerts and IOC are configured exactly as Account Takeovers are.
  FRAUD_ALERTS: { valid: ATO_VALID_OPTIONS, action: ATO_ACTION_OPTIONS },
  IOC: { valid: ATO_VALID_OPTIONS, action: ATO_ACTION_OPTIONS },
};

/** The label for a field, whichever group it belongs to. */
export const customFeedbackOptionLabel = (
  group: CustomFeedbackGroup,
  field: string
): string =>
  group === 'valid'
    ? (CUSTOM_FEEDBACK_VALID_LABELS[field as CustomFeedbackValidField] ?? field)
    : (CUSTOM_FEEDBACK_ACTION_LABELS[field as CustomFeedbackActionField] ??
      field);

/** A group validator, not `Validators.required` on each control: the row lights up red
 * without every blank field turning up by name in the errors panel. */
export const customFeedbackRowValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const { option, custom } = (control.value ?? {}) as CustomFeedbackRow;

  return option && custom?.trim() ? null : { incompleteRow: true };
};

export interface CustomFeedbackRow {
  option: string;
  custom: string;
}

export const customFeedbackRowForm = (row?: Partial<CustomFeedbackRow>) =>
  new FormGroup(
    {
      option: new FormControl(row?.option ?? '', { nonNullable: true }),
      custom: new FormControl(row?.custom ?? '', { nonNullable: true }),
    },
    { validators: [customFeedbackRowValidator] }
  );

export type CustomFeedbackRowFormGroup = ReturnType<
  typeof customFeedbackRowForm
>;

/** Names the deliverables that still need a status and an action. */
export const customFeedbackErrorMessage = (
  missing: readonly CustomFeedbackEntitlement[]
): string =>
  `Custom feedback needs at least one status and one action for: ${missing
    .map(key => entitlementLabel(CUSTOM_FEEDBACK_ENTITLEMENT[key]))
    .join(', ')}`;

/** Filled in once any field has a label. Survives a field that is not a list, so bad data is
 * reported rather than thrown on. */
const hasAnyLabel = (fields: Record<string, string[]>): boolean =>
  Object.values(fields).some(labels => (labels ?? []).length > 0);

/** What the validator asks of a deliverable: at least one status and one action. */
export const hasCustomFeedbackLabels = (
  values: ReturnType<CustomFeedbackFormGroup['getRawValue']>['values'],
  key: CustomFeedbackEntitlement
): boolean => hasAnyLabel(values[key].valid) && hasAnyLabel(values[key].action);

export function customFeedbackValidator(
  control: AbstractControl
): ValidationErrors | null {
  const entitlements = control.parent?.get('entitlements')?.value as
    string[] | undefined;

  if (!entitlements?.length) return null;

  const { isActive, values } = (
    control as CustomFeedbackFormGroup
  ).getRawValue();

  if (!isActive) return null;

  const missing = (
    Object.keys(CUSTOM_FEEDBACK_ENTITLEMENT) as CustomFeedbackEntitlement[]
  ).filter(
    key =>
      entitlements.includes(CUSTOM_FEEDBACK_ENTITLEMENT[key]) &&
      !hasCustomFeedbackLabels(values, key)
  );

  const errors: ValidationErrors = {};

  if (missing.length) errors['invalidCustomFeedback'] = { missing };

  // A half-filled row is not written into `values`, so without this it would vanish on save
  // with nothing said. Rows are disabled while the section is off, so this only bites in use.
  if (control.get('rows')?.invalid) errors['incompleteCustomFeedback'] = true;

  return Object.keys(errors).length ? errors : null;
}

const impactNumber = (kind: ProtoNumberKind, value: number | null = null) =>
  new FormControl<number | null>(value, {
    validators: [protoNumberValidator(kind)],
  });

// Money and ROI are a `double` in the proto but a `long` in the index, and the index is the
// narrower of the two, so both bounds apply.
const impactAmount = (value: number | null = null) =>
  new FormControl<number | null>(value, {
    validators: [protoNumberValidator('double'), storedLongValidator],
  });

const impactForm = () =>
  new FormGroup({
    // The one 64-bit field here, so the only one that can arrive as a string.
    costOfEngagement: new FormControl<LongNumberValue>(0, {
      validators: [protoNumberValidator('uint64'), storedLongValidator],
    }),

    showCardsImpactSavings: new FormControl(true, { nonNullable: true }),
    fraudCostPerCard: impactAmount(0),
    activeCards: new FormControl(true, { nonNullable: true }),
    activeCardsOverride: impactNumber('uint32', 70),

    showAtosImpactSavings: new FormControl(true, { nonNullable: true }),
    interOpCostPerAccount: impactAmount(),
    fraudCostPerAccount: impactAmount(),
    atoMaximumRoiValue: impactAmount(),
    atoMinimumRoiValue: impactAmount(),
    atoThreshold: impactNumber('uint32'),
    activeAtos: new FormControl(true, { nonNullable: true }),
    activeAtosOverride: impactNumber('uint32'),
  });

export const portalConfigForm = () => {
  return customFormGroup(
    {
      entitlements: portalEntitlementsControl(),
      display: new FormGroup({
        defaultLanguage: new FormControl('EN', { nonNullable: true }),
        mapCenterLocation: new FormControl(DEFAULT_MAP_CENTER, {
          nonNullable: true,
        }),
      }),
      clientSettings: new FormGroup({
        mfa: new FormGroup({
          required: new FormControl(false, { nonNullable: true }),
        }),
        externalSso: new FormGroup({
          enabled: new FormControl(false, { nonNullable: true }),
        }),
      }),
      // Declared rather than left to `customPatchValue`'s dynamic controls: the section
      // edits these now, so the shape has to exist before the client is patched in.
      customFeedback: customFeedbackForm(),
      impact: impactForm(),
    },
    {
      errorMessages: [
        {
          fieldKey: 'entitlements',
          errorName: 'required',
          message: 'At least one deliverable is required',
        },
        {
          fieldKey: 'entitlements',
          errorName: 'invalidEntitlementRequirement',
          message: error =>
            entitlementRequirementsErrorMessage(
              error.error_value?.dependents ?? []
            ),
        },
        {
          fieldKey: 'customFeedback',
          errorName: 'invalidCustomFeedback',
          message: error =>
            customFeedbackErrorMessage(error.error_value?.missing ?? []),
        },
        {
          fieldKey: 'customFeedback',
          errorName: 'incompleteCustomFeedback',
          message: 'Custom feedback has an item missing a label or an option',
        },
      ],
    }
  );
};

export type PortalImpactFormGroup = PortalConfigFormGroup['controls']['impact'];

/** Derived from the factory so a change to `portalConfigForm()` flows through. */
export type PortalDisplayFormGroup =
  PortalConfigFormGroup['controls']['display'];
export type PortalClientSettingsFormGroup =
  PortalConfigFormGroup['controls']['clientSettings'];

/** One entitlement set applied to many users, held to the same rules as a single user's. */
export const portalBulkEntitlementsForm = portalEntitlementsControl;

export type PortalUserFormGroup = ReturnType<typeof portalUserForm>;

/** Not proto-backed — see `PortalUser` above. */
/**
 * Keycloak refuses these in a person's name, so the field says so rather than the save
 * failing. The apostrophe it also refuses is left alone — real names carry it.
 */
const NAME_PROHIBITED_CHARACTERS = [
  '<',
  '>',
  '&',
  '"',
  '$',
  '%',
  '!',
  '#',
  '?',
  ';',
  '*',
  '~',
  '/',
  '\\',
  '|',
  '^',
  '=',
  '[',
  ']',
  '{',
  '}',
  '(',
  ')',
];

export const portalUserForm = () => {
  return customFormGroup(
    {
      id: new FormControl(''),
      firstName: new FormControl('', [
        Validators.required,
        disallowedCharactersValidator(NAME_PROHIBITED_CHARACTERS),
      ]),
      lastName: new FormControl('', [
        Validators.required,
        disallowedCharactersValidator(NAME_PROHIBITED_CHARACTERS),
      ]),
      email: new FormControl('', [Validators.required, Validators.email]),
      roles: new FormControl<string[]>([], {
        nonNullable: true,
        validators: [Validators.required],
      }),
      entitlements: portalEntitlementsControl(),
    },
    {
      errorMessages: [
        {
          fieldKey: 'firstName',
          errorName: 'required',
          message: 'First name is required',
        },
        {
          fieldKey: 'lastName',
          errorName: 'required',
          message: 'Last name is required',
        },
        {
          fieldKey: 'email',
          errorName: 'required',
          message: 'Email is required',
        },
        {
          fieldKey: 'email',
          errorName: 'email',
          message: 'Email must be a valid address',
        },
        {
          fieldKey: 'roles',
          errorName: 'required',
          message: 'A role is required',
        },
        {
          fieldKey: 'entitlements',
          errorName: 'required',
          message: 'At least one deliverable is required',
        },
        {
          fieldKey: 'entitlements',
          errorName: 'invalidEntitlementRequirement',
          message: error =>
            entitlementRequirementsErrorMessage(
              error.error_value?.dependents ?? []
            ),
        },
      ],
    }
  );
};
